@import './variables.scss';

.iono-player {

    /* VARS */

    --fluid-type-scale: 1;
    --font-family: #{$fontFamily};
    --font-weight: #{$fontWeight};
    --primary: #{$accent};
    --primary-contrast: #fff;
    --primary-muted: color-mix(in srgb, var(--primary) 60%, transparent);
    --border: #{$border};
    --muted: color-mix(in srgb, var(--text) 60%, transparent);
    --muted-controls: color-mix(in srgb, var(--text) 20%, transparent);
    --light: #efefef;
    --text: #{$text};
    --fade-out-ms: 150ms;
    --background: #{$background};
    --border-radius: 5px;

    /* RESET */

    &, *, *::before, *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
    }

    contain: content;
    max-height: 100%;
    min-width: 290px;
    font-weight: $fontWeight;
    font-size: $fontSize;
    font-family: $fontFamily;
    line-height: $lineHeight;
    color: var(--text);
    background-color: var(--background);
    border: 1px solid var(--border);
    border-radius: var(--border-radius);
    user-select: none;
    accent-color: var(--primary);
    opacity: 1;
    transition: opacity 300ms;
    overflow: hidden;
    
    // -webkit-font-smoothing: subpixel-antialiased;
    // -moz-osx-font-smoothing: grayscale;
    // text-rendering: optimizeLegibility;
    // @supports (font-size-adjust: 1) {
    //     .iono-player {
    //         font-size-adjust: 0.5;
    //     }
    // }

    &:not([data-state]),
    &[data-state="notready"],
    &[data-state="empty"] {
        opacity: 0;
    }

    [hidden] {
        display: none!important;
    }

    :focus {
        outline: 0;
        // outline: 2px solid var(--text);
        // outline-offset: -2px;
    }

    /* COMPONENTS */

    .iono- {
        &button {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: em(4px);
            font-size: 16px;//scale-font(16px, 18px);
            font-family: var(--font-family);
            font-weight: var(--font-weight);
            line-height: 1;
            // min-width: 30px;
            text-decoration: none;
            white-space: nowrap;
            border-radius: 9999px;
            border: none;
            background: none;
            padding: em(7px);
            color: currentColor;
            cursor: pointer;
            transition: background-color $fadeOutDuration linear;
            
            & * {
                pointer-events: none;
            }
        
            &:hover {
                background: var(--light);
                color: var(--primary);

                & .iono-text-muted {
                    color: var(--primary-muted);
                }
            }

            &:active, &.iono-active {
                background: var(--text);
                color: var(--background);
            }
        
            &:disabled {
                background: none;
                color: var(--muted-controls);
                opacity: 1;
                pointer-events: none;
            }
        
            svg, .iono-icon {
                vertical-align: top;
                width: 1em;
                height: 1em;
                flex-shrink: 0;
            }

            &-link {
                line-height: 1.25;
                padding: 0;

                &:hover, &:active, &.iono-active {
                    background: transparent;
                    color: var(--primary);
                }
            }


            &-label {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 100%;
                font-size: em(16px, 18px);
                line-height: 1.25;
            }
        }

        &icon {
            width: 1em;
            height: 1em;
            vertical-align: top;
        }

        &button-group {
            display: flex;
            align-items: center;
            gap: em(15px);
        }
    
        &link {
            color: var(--primary);
            text-decoration: none;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }

        &brand {
            display: inline-flex;
            align-items: center;
            color: var(--text);
            fill: var(--text);
            background: none;
            border: none;
            font-size: 1em;
            height: 1.25em;
            
            &:is(a) {
                cursor: pointer;
            }

            &-image {
                height: 1em;
                width: auto;
            }

            &--logo .iono-brand-image {
                height: .75em;
            }
        }
    
        &button-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(#{"min(100% / 1, max(200px, 100% / 5))"}, 1fr));
            gap: em(12px);
        }
    
        &state-button {
            aspect-ratio: 1 / 1;
            border: none;
            background: none;
            color: var(--primary);
            cursor: pointer;
            border-radius: 9999px;
            flex: 0 0 auto;

            & > .iono-icon {
                width: 1em;
                height: 1em;
                display: inline-block;
            }
        }

        &metadata-explicit > svg {
            height: .75em;
            width: auto;
        }
    
        &playlist-title,
        &metadata-subtitle {
            font-size: em(12px);
            // line-height: 1.2;
            letter-spacing: em(.3, 12);
            color: var(--muted);
            text-transform: uppercase;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            margin-top: em(6px, 12px);
        }

        &metadata-subtitle:empty {
            display: none;
        }

        &metadata-description {
            font-size: em(14px);
            line-height: 1.5;
            white-space: pre-line;
            word-break: break-word;
            text-wrap: balance;
        }

        &position,
        &duration {
            // line-height: 1;
            font-variant-numeric: tabular-nums;
        }
    
        &popover {
            background: var(--background);
            padding: 5px;
            border-radius: 5px;
            border: 1px solid var(--border);
        }
    
        &slider {
            --slider-track-size: 5px;
            --slider-track-color: var(--muted-controls);
            --slider-bar-color: var(--text);
            
            --slider-thumb-size: 20px;
            --slider-thumb-color: var(--text);
            
            position: relative;
            display: flex;
            align-items: center;
            min-height: var(--slider-thumb-size);
            touch-action: none;
            // margin: 0 calc(var(--slider-thumb-size) / 2);
            
            &-track {
                height: var(--slider-track-size);
                background-color: var(--slider-track-color)!important;
                border-radius: var(--slider-track-size);
                contain: content;
                width: 100%;
            }
            
            &-thumb {
                width: var(--slider-thumb-size);
                height: var(--slider-thumb-size);
                padding: 0;
                background: var(--slider-thumb-color);
                border: 0;
                position: absolute;
                z-index: 2;
                border-radius: var(--slider-thumb-size);
                left: 0;
                will-change: left;
                transform: translateX(-50%);
            }
            
            &-bar {
                height: var(--slider-track-size);
                background-color: var(--slider-bar-color);
                border-radius: var(--slider-track-size);
                position: absolute;
                width: 0%;
                will-change: width;
            }       
        }
    
        &volume {
            position: relative;

            &-popover {
                padding-left: 15px;
                padding-right: 15px;
                border-radius: 9999px;
            }
        }
    
        &timeline {
            gap: em(10px);
            flex: 1 1 auto;
            display: flex;
            align-items: center;

            .iono-position,
            .iono-duration {
                font-size: em(14px);
            }
        
            .iono-slider {
                --slider-thumb-color: var(--primary);
                --slider-bar-color: var(--primary);
        
                flex: 1 1 auto;

                .iono-slider-thumb:after {
                    content: "";
                    display: block;
                    width: 40px;
                    height: 40px;
                    background: var(--slider-thumb-color);
                    opacity: .3;
                    left: -10px;
                    top: -10px;
                    border-radius: 40px;
                    position: relative;
                }
            }

            .iono-slider-thumb {
                opacity: 0;
                will-change: opacity;
                transition: opacity $fadeInDuration * 2;
                pointer-events: none;
            }
        
            &[data-seekable] .iono-slider:hover .iono-slider-thumb,
            &[data-seekable] .iono-slider[data-sliding] .iono-slider-thumb {
                opacity: 1;
                pointer-events: auto;
            }

            &--live .iono-waveform-position-container {
                width: 100%;
            }

            &-tracks {
                display: flex;
                gap: em(2px);
                /* contain: content; */
                border-radius: 5px;
                width: 100%;
                align-items: flex-start;
            }
            
            &-track {
                flex-grow: 0;
                transition: transform .15s;
                will-change: transform;
                contain: content;
                position: relative;
                min-width: 2px;
            }

            &-track:not(:first-child, :last-child) {
                border-radius: 0;
            }
            
            &-track:first-child {
                border-top-left-radius: var(--slider-track-size);
                border-bottom-left-radius: var(--slider-track-size);
            }
            
            &-track:last-child {
                border-top-right-radius: var(--slider-track-size);
                border-bottom-right-radius: var(--slider-track-size);
            }
            
            &-track:hover {
                transform: scale(1, 1.25);
            }
            
            &-progress, &-position {
                transform: scale(0, 1);
                will-change: transform;
                transform-origin: 0 0;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
                border-radius: 0;
                /* transition: scale 0.15s; */
            }
        
            &-position {
                z-index: 2;
            }
        
            &-progress {
                background-color: var(--primary-muted);
            }
        }
    
        &tooltip {
            background: var(--text);
            color: var(--background);
            font-weight: bold;
            font-size: em(12px);
            border-radius: 4px;
            padding: em(5px, 12px);
            // max-width: 80%;
            pointer-events: none;
            touch-action: none;
            
            width: max-content;
            position: absolute;
            top: 0;
            left: 0;

            &-arrow {
                position: absolute;
                background: var(--text);
                width: 8px;
                height: 8px;
            }
        }
    
        &crossfade-image {
            background-size: cover;

            img {
                vertical-align: top;
                width: 100%;
                height: auto;

                &[src=""] {
                    opacity: 0;
                }
            }
        }

        &marquee {
            overflow: hidden;
            display: flex;

            & > div {
                padding-right: 50px;
                white-space: nowrap;
                will-change: transform;
            }
    
            & > div:nth-child(n+2) {
                display: none;
            }
    
            &.iono-animate > div {
                display: block;
                animation-name: iono-marquee;
                animation-timing-function: linear;
                animation-iteration-count: infinite;
            }
        }

        &playlist {
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            border-top: 1px solid var(--border);

            // @supports (clip-path: polygon(0 0, 100% 0, 100% 200%, 0 200%)) {
            //     &-header {
            //         box-shadow: 0 0px 8px 0px rgba(0, 0, 0, 0.15);
            //         clip-path: polygon(0 0, 100% 0, 100% 200%, 0 200%);
            //     }
            // }

            
            &-header {
                display: flex;
                align-items: center;
                padding: em(8px) em(12px);
                border-bottom: 1px solid var(--border);
                gap: em(12px);
            }

            &-title {
                flex-grow: 1;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                margin-top: 0;
            }

            &-items {
                display: flex;
                flex-direction: column;
                flex: 1 1 auto;
                overflow: auto;
                scroll-behavior: smooth;
            }

            &-item,
            &-load-more {
                display: flex;
                align-items: center;
                padding: em(8px) em(12px);
                will-change: background-color, color;
                transition: background-color $fadeInDuration, color $fadeInDuration;

                &:hover {
                    background-color: var(--light);
                }
            }

            &-item {
                cursor: pointer;                
            
                & + .iono-playlist-item {
                    border-top: 1px solid var(--border);
                }
            
                .iono-metadata {
                    flex-grow: 1;
                }
            
                .iono-metadata-title {
                    font-size: em(14px);
                }
            
                .iono-duration {
                    font-size: em(12px);
                    padding-left: em(15px, 12px);
                    color: var(--muted);
                }

                &.iono-active {
                    background-color: var(--background);
                    color: var(--primary);
                    cursor: default;

                    .iono-duration {
                        color: var(--primary-muted);
                    }
                }
            
                &.iono-active .iono-metadata-subtitle {
                    color: var(--primary-muted);
                }
            }

            &-load-more {
                width: 100%;
                padding: 1em em(12px);
                border-top: 1px solid var(--border);
                border-radius: 0;

                &:hover {
                    color: inherit;
                }
            }

            &-search {
                // gap: em(4px);
                display: flex;
                align-items: center;
                position: relative;
                min-width: em(30px);
                margin: -1px 0;
                border: 1px solid transparent;

                &-input {
                    width: 100%;
                    line-height: 1;
                    font-size: em(14px);
                    padding: em(6px, 14px) em(10px, 14px);
                    border: none;
                    background: transparent;
                    color: var(--text);
                    display: none;
                }

                &:focus-within {
                    border-color: var(--text);
                    border-radius: 9999px;

                    .iono-playlist-search-input {
                        display: block;
                    }
                }
            }
        }

        &waveforms {
            flex: 1 1 auto;
            position: relative;
            height: 30px;
            overflow: hidden;
            z-index: 0;
        }
    
        &waveforms > * {
            width: 100%;
            height: 100%;
            position: absolute;
            left: 0;
            top: 0;
        }
    
        &waveform-position-container,
        &waveform-buffered-container {
            width: 0%;
            contain: content;
        }
    
        &waveform-position-container {
            z-index: 2;
        }
    
        &waveform-buffered-container {
            z-index: 1;
        }

        &tabs {
            display:flex;
            align-items: center;
            flex-wrap: wrap;
            gap: em(12px);

            & > [data-tab], & > .iono-button-link {
                font-size: em(14px);
                text-transform: uppercase;

                &:not(:hover) {
                    color: var(--muted);
                }

                &:hover {
                    color: var(--primary);
                }
        
                &.iono-active {
                    color: var(--text);
                    text-decoration: underline;
                }
            }
        }


        &chapters-list,
        &transcript-list {
            font-size: em(14px);
            list-style-type: none;
            padding: 0;
        }
        
        &chapters-list > li + li {
            margin-top: em(8px, 14px);
        }

        &transcript-list > li {
            display: flex;
            gap: 1em;
        }

        &transcript-list > li + li {
            margin-top: 1em;
        }

        &playback-actions,
        &playlist-actions {
            display: flex;
            align-items: center;
        }

        &playback-actions {
            justify-content: flex-end;
        }

        &advert-controls {
            gap: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &advert-controls > svg {
            width: 2em;
            height: 2em;
        }
    
        &advert-controls .iono-advert-skip {
            margin-left: auto;
        }
    
        /* UTILS */
    
        &sr-only {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            border: 0;
            padding: 0;
            white-space: nowrap;
        }

        &flex {
            display: flex;
        }

        &gap {
            gap: em(10px);
        }

        &flex-fill {
            flex: 1 1 auto;
        }

        &flex-vertical {
            flex-direction: column;
        }

        &scrollable {
            overflow: auto;
        }

        &text-muted {
            color: var(--muted);
        }

        &ellipsis {
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
        }

        &advert-companion {
            flex-grow: 0;
            min-width: 0;
        }
    
    
        /* ANIMATIONS */
    
        &spin {
            animation: linear iono-spin $spinAnimationDuration infinite;
            transform-origin: 50%;
        }
    
        @keyframes iono-spin {
            0% {
                transform: rotateZ(0deg);
            }
            100% {
                transform: rotateZ(360deg);
            }
        }
    
        &fade-in {
            opacity: 1;
            animation: iono-fade-in $fadeInDuration * 2;
        }
    
        &fade-out {
            opacity: 0;
            animation: iono-fade-out $fadeOutDuration * 2;
        }

        &slide-up {
            animation: iono-slide-up $fadeOutDuration;
        }

        &slide-down {
            animation: iono-slide-down $fadeOutDuration;
        }
    
        @keyframes iono-marquee {
            0% {
                transform: translateX(0%);
            }
            100% {
                transform: translateX(-100%);
            }
        }
    
        @keyframes iono-fade-in {
            0% {
                opacity: 0;
            }
            100% {
                opacity: 1;
            }
        }
    
        @keyframes iono-fade-out {
            0% {
                opacity: 1;
            }
            100% {
                opacity: 0;
            }
        }

        @keyframes iono-slide-up {
            0% {
                transform: translateY(100%);
            }
            100% {
                transform: translateY(0%);
            }
        }

        @keyframes iono-slide-down {
            0% {
                transform: translateY(0%);
            }
            100% {
                transform: translateY(100%);
            }
        }
    }

}